import { clsx } from "clsx";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

/**
 * cn - Class Name Merge Utility
 * ------------------------------------------------------------
 * Combines class names using clsx and merges Tailwind classes
 * to remove conflicts. Useful for conditional or dynamic classes.
 *
 * @param  {...any} inputs - List of class names (strings, objects, arrays)
 * @returns {string} - Merged class string
 *
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * formatDate - Format a date to a readable string
 * ------------------------------------------------------------
 * Converts a Date object or string to "MMM DD, YYYY" format.
 *
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 *
 */
export function formatDate(date) {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * debounce - Debounce function
 * ------------------------------------------------------------
 * Limits the rate at which a function can fire. Useful for API calls,
 * search input, or other events that fire frequently.
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 *

 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/* ───────────── Helper Components ───────────── */

/* ==
   HELPERS
== */
export function formatDDMonYYYY(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(d).padStart(2, "0")}-${months[date.getMonth()]}-${date.getFullYear()}`;
}

//helper function

export function formatDateInput(date) {
  if (!date) return "-- -- ----";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatTimeInput(date) {
  if (!date) return "--:-- --";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function get12Hour(hour24) {
  const h = hour24 % 12;
  return h === 0 ? 12 : h;
}

// format mac address

export const formatStatus = (type) => {
  if (!type) return "-";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};
export const formatDuration = (seconds) => {
  if (!seconds) return "-";

  return seconds <= 60 ? `${seconds} sec` : `${Math.ceil(seconds / 60)} min`;
};

export const getMediaFileName = (field, url = "") => {
  if (field?.fileName) return field.fileName;

  const mediaUrl =
    url ||
    field?.previewSrc ||
    field?.src ||
    field?.value ||
    field?.url ||
    field?.mediaUrl ||
    field?.fileUrl ||
    "";

  const cleanUrl = mediaUrl.split("?")[0];
  const fileName = cleanUrl.split("/").pop();

  return fileName ? decodeURIComponent(fileName) : "filename.format";
};
export const formatedStatus = (status = "") => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const PRIMARY_COLOR_STORAGE_KEY = "tenant-primary-colors";

const normalizeTenantId = (tenantId) => {
  if (!tenantId) return "";

  if (typeof tenantId === "object") {
    return String(tenantId._id || tenantId.id || "");
  }

  return String(tenantId);
};

export const getStoredPrimaryColors = () => {
  if (typeof window === "undefined") return {};

  try {
    const storedValue = localStorage.getItem(PRIMARY_COLOR_STORAGE_KEY);

    return storedValue ? JSON.parse(storedValue) : {};
  } catch (error) {
    console.error("Unable to read tenant primary colors:", error);

    return {};
  }
};

export const savePrimaryColor = (tenantId, color) => {
  if (typeof window === "undefined") return;

  const normalizedTenantId = normalizeTenantId(tenantId);

  if (!normalizedTenantId || !color) return;

  const colors = getStoredPrimaryColors();

  localStorage.setItem(
    PRIMARY_COLOR_STORAGE_KEY,
    JSON.stringify({
      ...colors,
      [normalizedTenantId]: color,
    }),
  );
};

export const getPrimaryColor = (tenantId) => {
  if (typeof window === "undefined") return null;

  const normalizedTenantId = normalizeTenantId(tenantId);

  if (!normalizedTenantId) return null;

  const colors = getStoredPrimaryColors();

  return colors[normalizedTenantId] || null;
};
